import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { signOut } from '../store/slices/authSlice'
import { colors } from '../constants/colors'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import editIcon from '../assets/images/edit.png'
import deleteIcon from '../assets/images/delete.png'
import { 
  fetchIntents,
  createIntent,
  updateIntent,
  deleteIntent,
  clearError,
  clearSuccess
} from '../store/slices/intentSlice'
import NotificationModal from '../components/NotificationModal'

const IntentManagement = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { intents: rawIntents, loading: isLoading, error, success } = useSelector((state) => state.intent)
  
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingIntent, setEditingIntent] = useState(null)
  const [formData, setFormData] = useState({
    intentName: '',
    trainingPhrases: '',
    botResponse: '',
    responseType: 'Text'
  })
  const [intents, setIntents] = useState([])
  
  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  
  const [isSigningOut, setIsSigningOut] = useState(false)

  // Notification modal state
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    showCancel: false,
    onConfirm: null
  })

  // Fetch intents on component mount
  useEffect(() => {
    dispatch(fetchIntents())
  }, [dispatch])

  // Transform intents data
  useEffect(() => {
    if (rawIntents && rawIntents.length > 0) {
      const transformedData = rawIntents.map(intent => ({
        id: intent._id,
        name: intent.name,
        sampleQuestions: intent.sample_questions?.join(', ') || '',
        responseType: intent.response_type,
        lastUpdate: new Date(intent.updated_at || intent.created_at).toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: '2-digit'
        }),
        botResponse: intent.bot_response,
        responseMediaUrl: intent.response_media_url
      }))
      setIntents(transformedData)
    } else {
      setIntents([])
    }
  }, [rawIntents])

  // Show notifications for errors and success
  useEffect(() => {
    if (error) {
      showNotification('error', 'Error', error)
      dispatch(clearError())
    }
    if (success) {
      showNotification('success', 'Success', success)
      dispatch(clearSuccess())
      dispatch(fetchIntents()) // Refresh intents
    }
  }, [error, success, dispatch])

  // Helper functions for notifications
  const showNotification = (type, title, message, showCancel = false, onConfirm = null) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message,
      showCancel,
      onConfirm
    })
  }

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }))
  }

  const handleEdit = (intentId) => {
    const intent = intents.find(i => i.id === intentId)
    if (intent) {
      setEditingIntent(intent)
      setFormData({
        intentName: intent.name,
        trainingPhrases: intent.sampleQuestions,
        botResponse: intent.botResponse,
        responseType: intent.responseType
      })
      setIsEditMode(true)
      setIsModalOpen(true)
    }
  }

  const handleDelete = (intentId) => {
    const intent = intents.find(i => i.id === intentId)
    if (intent) {
      setItemToDelete(intent)
      setIsDeleteModalOpen(true)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return

    dispatch(deleteIntent(itemToDelete.id)).then(() => {
      setIsDeleteModalOpen(false)
      setItemToDelete(null)
    })
  }

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false)
    setItemToDelete(null)
  }

  const handleAddIntent = () => {
    setIsEditMode(false)
    setEditingIntent(null)
    setFormData({
      intentName: '',
      trainingPhrases: '',
      botResponse: '',
      responseType: 'Text'
    })
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setIsEditMode(false)
    setEditingIntent(null)
    setFormData({
      intentName: '',
      trainingPhrases: '',
      botResponse: '',
      responseType: 'Text'
    })
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.intentName.trim() || !formData.trainingPhrases.trim() || !formData.botResponse.trim()) {
      showNotification('warning', 'Validation Error', 'Please fill in all fields')
      return
    }

    const intentData = {
      name: formData.intentName.trim(),
      sample_questions: formData.trainingPhrases.split(',').map(phrase => phrase.trim()).filter(phrase => phrase),
      response_type: formData.responseType,
      bot_response: formData.botResponse.trim()
    }

    if (isEditMode && editingIntent) {
      dispatch(updateIntent({ id: editingIntent.id, intentData })).then(() => {
        handleCloseModal()
      })
    } else {
      dispatch(createIntent(intentData)).then(() => {
        handleCloseModal()
      })
    }
  }

  const handleSignOut = async () => {
    setIsSigningOut(true)
    try {
      await dispatch(signOut()).unwrap()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setTimeout(() => {
        navigate('/signin')
        setIsSigningOut(false)
      }, 500)
    }
  }

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: colors.background.secondary,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'flex'
  }

  const mainContentStyle = {
    flex: 1,
    marginLeft: '280px',
    padding: '24px',
    backgroundColor: colors.background.secondary
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  }

  const headerLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const headerRightStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const headerIconStyle = {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    color: colors.text.secondary
  }

  const notificationIconStyle = {
    ...headerIconStyle,
    position: 'relative'
  }

  const notificationDotStyle = {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    width: '8px',
    height: '8px',
    backgroundColor: '#ef4444',
    borderRadius: '50%'
  }

  const addButtonStyle = {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.background.primary,
    backgroundColor: colors.primary,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }

  const titleStyle = {
    fontSize: '32px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: 0
  }

  const tableContainerStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  }

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  }

  const tableHeaderStyle = {
    backgroundColor: colors.background.tertiary,
    padding: '16px 24px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    borderBottom: `1px solid ${colors.border}`
  }

  const tableRowStyle = {
    borderBottom: `1px solid ${colors.borderVariants.light}`
  }

  const tableCellStyle = {
    padding: '16px 24px',
    fontSize: '14px',
    color: colors.text.primary
  }

  const intentNameStyle = {
    fontWeight: '600',
    color: colors.text.primary
  }

  const responseTypeStyle = {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    backgroundColor: colors.background.tertiary,
    color: colors.text.secondary,
    display: 'inline-block'
  }

  const actionButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '8px',
    transition: 'background-color 0.2s ease'
  }

  const actionIconStyle = {
    width: '16px',
    height: '16px',
    opacity: 0.7
  }

  // Modal styles
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  }

  const modalStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '12px',
    padding: '32px',
    width: '500px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
  }

  const modalTitleStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: '0 0 24px 0'
  }

  const formGroupStyle = {
    marginBottom: '20px'
  }

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: '8px'
  }

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: `1px solid ${colors.borderVariants.medium}`,
    borderRadius: '8px',
    backgroundColor: colors.background.primary,
    color: colors.text.primary,
    outline: 'none',
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box'
  }

  const textareaStyle = {
    ...inputStyle,
    minHeight: '100px',
    resize: 'vertical',
    fontFamily: 'inherit'
  }

  const buttonGroupStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px'
  }

  const cancelButtonStyle = {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    backgroundColor: colors.background.primary,
    border: `1px solid ${colors.borderVariants.medium}`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  }

  const submitButtonStyle = {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.background.primary,
    backgroundColor: colors.primary,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  // Delete modal styles (matching AITrainingData)
  const deleteModalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001
  }

  const deleteModalStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '12px',
    padding: '32px',
    width: '400px',
    maxWidth: '90vw',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  }

  const deleteModalTitleStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: '0 0 16px 0'
  }

  const deleteModalTextStyle = {
    fontSize: '16px',
    color: colors.text.secondary,
    margin: '0 0 24px 0',
    lineHeight: '1.5'
  }

  const deleteModalButtonGroupStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  }

  const deleteCancelButtonStyle = {
    backgroundColor: 'white',
    color: colors.text.primary,
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const deleteConfirmButtonStyle = {
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    opacity: isLoading ? 0.7 : 1
  }

  return (
    <div style={containerStyle}>
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main Content */}
      <div style={mainContentStyle}>
        {/* Header */}
        <PageTopBar
          title="Intent Management"
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
          onSignOut={handleSignOut}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
          <button style={addButtonStyle} onClick={handleAddIntent}>
            + Add Intent
          </button>
        </div>

        {/* Intent Table */}
        <div style={tableContainerStyle}>
          {isLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.text.secondary }}>
              Loading intents...
            </div>
          ) : error ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>
              Error: {error}
            </div>
          ) : intents.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.text.secondary }}>
              No intents found. Create your first intent!
            </div>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Intent Name</th>
                  <th style={tableHeaderStyle}>Sample Questions</th>
                  <th style={tableHeaderStyle}>Response Type</th>
                  <th style={tableHeaderStyle}>Last Update</th>
                  <th style={tableHeaderStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {intents.map((intent) => (
                  <tr key={intent.id} style={tableRowStyle}>
                    <td style={tableCellStyle}>
                      <span style={intentNameStyle}>{intent.name}</span>
                    </td>
                    <td style={tableCellStyle}>{intent.sampleQuestions}</td>
                    <td style={tableCellStyle}>
                      <span style={responseTypeStyle}>{intent.responseType}</span>
                    </td>
                    <td style={tableCellStyle}>{intent.lastUpdate}</td>
                    <td style={tableCellStyle}>
                      <button 
                        style={actionButtonStyle}
                        onClick={() => handleEdit(intent.id)}
                        onMouseEnter={(e) => e.target.style.backgroundColor = colors.background.tertiary}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <img 
                          src={editIcon} 
                          alt="Edit" 
                          style={actionIconStyle}
                        />
                      </button>
                      <button 
                        style={actionButtonStyle}
                        onClick={() => handleDelete(intent.id)}
                        onMouseEnter={(e) => e.target.style.backgroundColor = colors.background.tertiary}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <img 
                          src={deleteIcon} 
                          alt="Delete" 
                          style={actionIconStyle}
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add Intent Modal */}
      {isModalOpen && (
        <div style={modalOverlayStyle} onClick={handleCloseModal}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>{isEditMode ? 'Edit Intent' : 'Add Intent'}</h2>
            
            <form onSubmit={handleSubmit}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="intentName">Intent Name</label>
                <input
                  type="text"
                  id="intentName"
                  name="intentName"
                  value={formData.intentName}
                  onChange={handleInputChange}
                  placeholder="Intent Name"
                  style={inputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="trainingPhrases">Training Phrases</label>
                <input
                  type="text"
                  id="trainingPhrases"
                  name="trainingPhrases"
                  value={formData.trainingPhrases}
                  onChange={handleInputChange}
                  placeholder="Add Phrases"
                  style={inputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="responseType">Response Type</label>
                <select
                  id="responseType"
                  name="responseType"
                  value={formData.responseType}
                  onChange={handleInputChange}
                  style={inputStyle}
                  required
                >
                  <option value="Text">Text</option>
                  <option value="Image">Image</option>
                  <option value="Video">Video</option>
                  <option value="Audio">Audio</option>
                </select>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="botResponse">Bot Response</label>
                <textarea
                  id="botResponse"
                  name="botResponse"
                  value={formData.botResponse}
                  onChange={handleInputChange}
                  placeholder="Enter bot response..."
                  style={textareaStyle}
                  required
                />
              </div>

              <div style={buttonGroupStyle}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={cancelButtonStyle}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = colors.background.tertiary
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = colors.background.primary
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={submitButtonStyle}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = colors.primaryDark
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = colors.primary
                  }}
                >
                  {isEditMode ? 'Update Intent' : 'Create Intent'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && itemToDelete && (
        <div style={deleteModalOverlayStyle}>
          <div style={deleteModalStyle}>
            <h2 style={deleteModalTitleStyle}>Delete Intent</h2>
            <p style={deleteModalTextStyle}>
              Are you sure you want to delete <strong>"{itemToDelete.name}"</strong>? 
              This action cannot be undone and will remove all associated data.
            </p>
            <div style={deleteModalButtonGroupStyle}>
              <button 
                style={deleteCancelButtonStyle} 
                onClick={handleDeleteCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                style={deleteConfirmButtonStyle} 
                onClick={handleDeleteConfirm}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        showCancel={notification.showCancel}
        onConfirm={notification.onConfirm}
      />
    </div>
  )
}

export default IntentManagement
